package com.havn.app.core.platform

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import com.havn.app.core.supabase.havnMapKitJsToken
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement

@Composable
actual fun HavnPropertyMap(
    address: String,
    searchRequestId: Int,
    markerTitle: String,
    missingTokenMessage: String,
    loadingMessage: String,
    coordinate: HavnMapCoordinate?,
    onCoordinateChanged: (HavnMapCoordinate) -> Unit,
    modifier: Modifier
) {
    val theme = LocalHavnThemeTokens.current
    var viewport by remember { mutableStateOf<Rect?>(null) }
    val isViewportReady = viewport.isUsableMapViewport()
    val viewportWidthBucket = viewport?.width?.toInt()?.div(200) ?: 0
    val mapShellElement = remember {
        (document.createElement("div") as HTMLDivElement).apply {
            className = "havn-mapkit-js-shell"
            style.position = "fixed"
            style.zIndex = "40"
            style.setProperty("overflow", "hidden")
            style.setProperty("contain", "paint")
            style.borderRadius = "8px"
            style.background = "#101820"
            style.display = "none"
            style.setProperty("pointer-events", "none")
        }
    }
    val mapElement = remember {
        (document.createElement("div") as HTMLDivElement).apply {
            className = "havn-mapkit-js-map"
            style.position = "absolute"
            style.left = "0"
            style.top = "0"
            style.width = "100%"
            style.height = "100%"
            style.setProperty("overflow", "hidden")
            style.background = "#101820"
        }
    }
    var controller by remember { mutableStateOf<dynamic>(null) }

    Box(
        modifier = modifier
            .background(theme.surface)
            .onGloballyPositioned { coordinates ->
                val nextViewport = coordinates.boundsInWindow()
                if (!viewport.isSameViewport(nextViewport)) {
                    viewport = nextViewport
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = if (havnMapKitJsToken.isBlank()) {
                missingTokenMessage
            } else {
                coordinate?.let { "${it.latitude.formatCoordinate()}, ${it.longitude.formatCoordinate()}" }
                    ?: address.ifBlank { loadingMessage }
            },
            color = theme.secondaryText,
            style = MaterialTheme.typography.bodyMedium
        )
    }

    DisposableEffect(mapElement) {
        consoleLog("compose mount", "tokenLength" to havnMapKitJsToken.length)
        mapShellElement.appendChild(mapElement)
        document.body?.appendChild(mapShellElement)
        onDispose {
            consoleLog("compose dispose")
            invokeControllerDispose(controller)
            mapShellElement.style.display = "none"
            mapShellElement.style.setProperty("pointer-events", "none")
            mapShellElement.remove()
        }
    }

    LaunchedEffect(viewport) {
        consoleLog("viewport", "bounds" to viewport?.toLogString())
        mapShellElement.applyViewport(viewport)
        window.requestAnimationFrame {
            consoleLog("resize requestAnimationFrame")
            controller?.resize?.invoke()
        }
    }

    LaunchedEffect(havnMapKitJsToken, isViewportReady, viewportWidthBucket) {
        if (havnMapKitJsToken.isBlank()) {
            consoleLog("missing token")
            mapShellElement.showMissingToken(missingTokenMessage)
            return@LaunchedEffect
        }
        if (!isViewportReady) {
            consoleLog("mount skipped until viewport ready", "bounds" to viewport?.toLogString())
            return@LaunchedEffect
        }
        if (controller != null) {
            consoleLog("mount skipped because controller exists")
            return@LaunchedEffect
        }
        mapShellElement.applyViewport(viewport)
        val initial = coordinate ?: HavnMapCoordinate(37.7749, -122.4194)
        consoleLog(
            "mount requested",
            "tokenLength" to havnMapKitJsToken.length,
            "initial" to "${initial.latitude.formatCoordinate()}, ${initial.longitude.formatCoordinate()}",
            "hasCoordinate" to (coordinate != null)
        )
        controller = try {
            mountMapKitMap(
                element = mapElement,
                token = havnMapKitJsToken,
                markerTitle = markerTitle,
                latitude = initial.latitude,
                longitude = initial.longitude,
                onCoordinateChanged = { latitude, longitude ->
                    onCoordinateChanged(HavnMapCoordinate(latitude, longitude))
                }
            )
        } catch (error: Throwable) {
            consoleLog("mount threw", "message" to error.message)
            null
        }
        consoleLog("mount returned", "hasController" to (controller != null))
        if (coordinate == null && address.isNotBlank()) {
            consoleLog("initial search", "queryLength" to address.length)
            invokeControllerSearch(controller, address)
        }
    }

    LaunchedEffect(coordinate) {
        coordinate?.let {
            consoleLog("coordinate effect", "coordinate" to "${it.latitude.formatCoordinate()}, ${it.longitude.formatCoordinate()}")
            invokeControllerSetMarker(controller, it.latitude, it.longitude)
        }
    }

    LaunchedEffect(address, searchRequestId) {
        if (searchRequestId > 0 && address.isNotBlank()) {
            consoleLog("search effect", "requestId" to searchRequestId, "queryLength" to address.length)
            invokeControllerSearch(controller, address)
        }
    }
}

private fun HTMLDivElement.applyViewport(viewport: Rect?) {
    val bounds = viewport?.takeIf { it.isUsableMapViewport() }
    if (bounds == null) {
        style.display = "none"
        style.setProperty("pointer-events", "none")
        return
    }
    style.display = "block"
    style.setProperty("pointer-events", "auto")
    style.left = "${bounds.left}px"
    style.top = "${bounds.top}px"
    style.width = "${bounds.width}px"
    style.height = "${bounds.height}px"
    style.maxWidth = "${bounds.width}px"
    style.maxHeight = "${bounds.height}px"
}

private fun Rect?.isSameViewport(other: Rect): Boolean {
    val current = this ?: return false
    return current.left.closeTo(other.left) &&
        current.top.closeTo(other.top) &&
        current.width.closeTo(other.width) &&
        current.height.closeTo(other.height)
}

private fun Rect?.isUsableMapViewport(): Boolean =
    this?.let { it.width >= 120f && it.height >= 120f } == true

private fun Float.closeTo(other: Float): Boolean =
    kotlin.math.abs(this - other) < 0.5f

private fun Rect.toLogString(): String =
    "left=${left.formatLogNumber()}, top=${top.formatLogNumber()}, width=${width.formatLogNumber()}, height=${height.formatLogNumber()}"

private fun Float.formatLogNumber(): String =
    (kotlin.math.round(this * 10f) / 10f).toString()

private fun consoleLog(message: String, vararg pairs: Pair<String, Any?>) {
    if (pairs.isEmpty()) {
        console.log("[HAVN MapKit] $message")
    } else {
        console.log("[HAVN MapKit] $message", jsLogObject(pairs))
    }
}

private fun jsLogObject(pairs: Array<out Pair<String, Any?>>): dynamic {
    val obj = js("({})")
    pairs.forEach { (key, value) -> obj[key] = value }
    return obj
}

private fun invokeControllerSearch(controller: dynamic, query: String) {
    val invoker = js(
        """
        (controller, query) => {
          console.log("[HAVN MapKit] search queued", { queryLength: query ? query.length : 0, hasController: !!controller });
          window.setTimeout(() => {
            try {
              if (!controller || typeof controller.search !== "function") {
                console.log("[HAVN MapKit] search queue missing controller");
                return;
              }
              controller.search(query);
            } catch (error) {
              console.error("[HAVN MapKit] search queue failed", { error });
            }
          }, 0);
        }
        """
    )
    invoker(controller, query)
}

private fun invokeControllerSetMarker(controller: dynamic, latitude: Double, longitude: Double) {
    val invoker = js(
        """
        (controller, latitude, longitude) => {
          console.log("[HAVN MapKit] set marker queued", { lat: latitude, lng: longitude, hasController: !!controller });
          window.setTimeout(() => {
            try {
              if (!controller || typeof controller.setMarker !== "function") {
                console.log("[HAVN MapKit] set marker queue missing controller");
                return;
              }
              controller.setMarker(latitude, longitude);
            } catch (error) {
              console.error("[HAVN MapKit] set marker queue failed", { error });
            }
          }, 0);
        }
        """
    )
    invoker(controller, latitude, longitude)
}

private fun invokeControllerDispose(controller: dynamic) {
    val invoker = js(
        """
        (controller) => {
          console.log("[HAVN MapKit] dispose queued", { hasController: !!controller });
          window.setTimeout(() => {
            try {
              if (!controller || typeof controller.dispose !== "function") {
                console.log("[HAVN MapKit] dispose queue missing controller");
                return;
              }
              controller.dispose();
            } catch (error) {
              console.error("[HAVN MapKit] dispose queue failed", { error });
            }
          }, 0);
        }
        """
    )
    invoker(controller)
}

private fun HTMLDivElement.showMissingToken(message: String) {
    style.display = "grid"
    style.setProperty("place-items", "center")
    style.padding = "24px"
    style.boxSizing = "border-box"
    style.color = "#B6B0A5"
    style.font = "14px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif"
    style.textAlign = "center"
    textContent = message
}

@Suppress("UnsafeCastFromDynamic")
private fun mountMapKitMap(
    element: HTMLDivElement,
    token: String,
    markerTitle: String,
    latitude: Double,
    longitude: Double,
    onCoordinateChanged: (Double, Double) -> Unit
): dynamic {
    val factory = js(
        """
        (element, token, markerTitle, latitude, longitude, onCoordinateChanged) => {
      const log = (message, details) => console.log("[HAVN MapKit] " + message, details || {});
      const errorLog = (message, error, details) => console.error("[HAVN MapKit] " + message, Object.assign({ error }, details || {}));

      log("mount start", {
        tokenLength: token ? token.length : 0,
        hasMapkit: !!(window.mapkit && window.mapkit.Map),
        latitude,
        longitude
      });

      element.innerHTML = "";
      element.style.padding = "0";
      element.style.display = "block";

      let map = null;
      let marker = null;
      let pendingMarker = { latitude, longitude };
      let pendingSearch = null;
      let disposed = false;

      function loadMapKit(callback) {
        if (window.mapkit && window.mapkit.Map) {
          log("script already loaded");
          callback();
          return;
        }
        const existing = document.querySelector("script[data-havn-mapkit-js='true']");
        if (existing) {
          log("script already injecting");
          existing.addEventListener("load", () => {
            log("existing script loaded");
            callback();
          }, { once: true });
          existing.addEventListener("error", event => {
            errorLog("existing script failed", event);
          }, { once: true });
          return;
        }
        const script = document.createElement("script");
        script.src = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";
        script.async = true;
        script.dataset.havnMapkitJs = "true";
        script.addEventListener("load", () => {
          log("script loaded");
          callback();
        }, { once: true });
        script.addEventListener("error", event => {
          errorLog("script failed", event, { src: script.src });
        }, { once: true });
        window.setTimeout(() => {
          if (!window.mapkit || !window.mapkit.Map) {
            log("script still waiting after timeout", { src: script.src });
          }
        }, 10000);
        log("script injecting", { src: script.src });
        document.head.appendChild(script);
      }

      function coordinate(lat, lng) {
        return new window.mapkit.Coordinate(lat, lng);
      }

      function setMarker(lat, lng, recenter) {
        log("set marker requested", { lat, lng, recenter, hasMap: !!map, hasMarker: !!marker });
        if (
          pendingMarker &&
          Math.abs(pendingMarker.latitude - lat) < 0.0000001 &&
          Math.abs(pendingMarker.longitude - lng) < 0.0000001
        ) {
          log("set marker skipped same coordinate");
          return;
        }
        pendingMarker = { latitude: lat, longitude: lng };
        if (!map || !marker) return;
        const next = coordinate(lat, lng);
        marker.coordinate = next;
        if (recenter) {
          map.setCenterAnimated(next);
        }
      }

      function search(query) {
        log("search requested", { queryLength: query ? query.length : 0, hasMap: !!map, hasMapkit: !!window.mapkit });
        pendingSearch = query;
        if (!query) return;
        window.setTimeout(() => {
          if (disposed) {
            log("search ignored after dispose");
            return;
          }
          runAddressLookup(query);
        }, 0);
      }

      function runAddressLookup(query) {
        const queries = addressLookupQueries(query);
        log("address lookup start", { queryLength: query.length, attempts: queries.length });
        const abortController = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timeoutId = window.setTimeout(() => {
          if (abortController) abortController.abort();
          log("address lookup timeout");
        }, 8000);

        lookupAttempt(queries, 0, abortController)
          .then(results => {
            window.clearTimeout(timeoutId);
            if (disposed) {
              log("address lookup ignored after dispose");
              return;
            }
            if (!Array.isArray(results) || !results.length) {
              log("address lookup empty");
              return;
            }
            const result = results[0];
            const lat = Number(result.lat);
            const lng = Number(result.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              log("address lookup invalid coordinate", { lat: result.lat, lon: result.lon });
              return;
            }
            log("address lookup result", { lat, lng, displayName: result.display_name });
            setMarker(lat, lng, true);
            onCoordinateChanged(lat, lng);
          })
          .catch(error => {
            window.clearTimeout(timeoutId);
            errorLog("address lookup failed", error);
          });
      }

      function lookupAttempt(queries, index, abortController) {
        const query = queries[index];
        if (!query) return Promise.resolve([]);
        const url = "https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=sk&q=" + encodeURIComponent(query);
        log("address lookup attempt", { index: index + 1, query });
        return fetch(url, {
          signal: abortController ? abortController.signal : undefined,
          headers: { "Accept": "application/json" }
        })
          .then(response => {
            log("address lookup response", { index: index + 1, status: response.status, ok: response.ok });
            if (!response.ok) throw new Error("Address lookup failed with status " + response.status);
            return response.json();
          })
          .then(results => {
            if (Array.isArray(results) && results.length) return results;
            return lookupAttempt(queries, index + 1, abortController);
          });
      }

      function addressLookupQueries(query) {
        const normalized = query
          .replace(/\s+/g, " ")
          .replace(/\s+-\s+/g, ", ")
          .trim();
        const withoutUnit = normalized
          .replace(/,\s*(unit|apt|apartment|suite)\s+[^,]+/i, "")
          .replace(/\b(unit|apt|apartment|suite)\s+[^,]+,?/i, "")
          .replace(/\s+/g, " ")
          .trim();
        const withoutCountry = withoutUnit.replace(/,\s*(SK|Slovakia|Slovensko)\s*$/i, "").trim();
        const withoutPostal = withoutCountry.replace(/,\s*\d{5}\s*$/i, "").trim();
        const street = normalized.split(",")[0].trim();
        const streetWithSlovakia = street ? street + ", Slovakia" : "";
        const streetWithBratislava = street ? street + ", Bratislava, Slovakia" : "";
        const diacritics = slovakAddressDiacritics(normalized);
        return Array.from(new Set([
          normalized,
          diacritics,
          withoutUnit,
          withoutCountry,
          withoutPostal,
          streetWithBratislava,
          slovakAddressDiacritics(streetWithBratislava),
          streetWithSlovakia,
          slovakAddressDiacritics(streetWithSlovakia)
        ].filter(Boolean)));
      }

      function slovakAddressDiacritics(query) {
        return query
          .replace(/\bGrosslingova\b/gi, "Grösslingová")
          .replace(/\bDunajska\b/gi, "Dunajská")
          .replace(/\bVinarska\b/gi, "Vinárska")
          .replace(/\bBratislavsky\b/gi, "Bratislavský")
          .replace(/\bStare Mesto\b/gi, "Staré Mesto")
          .replace(/\bNivy\b/gi, "Nivy");
      }

      function runSearch(query) {
        log("search start", { loadedLibraries: window.mapkit.loadedLibraries });
        const Geocoder = mapkitClass("Geocoder");
        const Search = mapkitClass("Search");
        try {
          if (Geocoder) {
            const geocoder = new Geocoder();
            log("geocoder lookup start");
            geocoder.lookup(query, handleSearchResult, { region: map.region });
            return;
          }
        } catch (error) {
          errorLog("geocoder failed to start", error);
        }
        try {
          if (Search) {
            const searchService = new Search({ region: map.region });
            log("search service start");
            searchService.search(query, handleSearchResult);
            return;
          }
        } catch (error) {
          errorLog("search failed to start", error);
        }
        log("search service unavailable");
      }

      function mapkitClass(name) {
        try {
          return window.mapkit && window.mapkit[name] ? window.mapkit[name] : null;
        } catch (error) {
          log("mapkit class unavailable", { name, message: error && error.message });
          return null;
        }
      }

      function handleSearchResult(error, data) {
        if (disposed) {
          log("search ignored after dispose");
          return;
        }
        if (error) {
          errorLog("search failed", error);
          return;
        }
        if (!data || !data.results || !data.results.length) {
          log("search empty");
          return;
        }
        const result = data.results[0];
        if (!result.coordinate) {
          log("search result without coordinate");
          return;
        }
        const lat = result.coordinate.latitude;
        const lng = result.coordinate.longitude;
        log("search result", { lat, lng });
        setMarker(lat, lng, true);
        onCoordinateChanged(lat, lng);
      }

      const controller = {
        setMarker: (lat, lng) => setMarker(lat, lng, false),
        search,
        resize: () => {
          log("resize", {
            hasMap: !!map,
            width: element.clientWidth,
            height: element.clientHeight
          });
          if (map && typeof map.resize === "function") map.resize();
        },
        dispose: () => {
          log("dispose");
          disposed = true;
          const mapToDestroy = map;
          map = null;
          marker = null;
          element.innerHTML = "";
          window.setTimeout(() => {
            try {
              if (mapToDestroy && typeof mapToDestroy.destroy === "function") {
                log("destroy map");
                mapToDestroy.destroy();
              }
            } catch (error) {
              errorLog("destroy map failed", error);
            }
          }, 0);
        }
      };

      window.setTimeout(() => {
        log("async mount begin", {
          width: element.clientWidth,
          height: element.clientHeight
        });
        loadMapKit(() => {
          if (disposed) return;
          try {
            log("init start", { tokenLength: token ? token.length : 0 });
            window.mapkit.init({
              libraries: ["full-map", "services"],
              authorizationCallback: done => {
                log("authorization callback");
                done(token);
              }
            });
            const start = coordinate(pendingMarker.latitude, pendingMarker.longitude);
            log("map create start", { latitude: start.latitude, longitude: start.longitude });
            map = new window.mapkit.Map(element, {
              center: start,
              showsCompass: window.mapkit.FeatureVisibility.Adaptive,
              showsMapTypeControl: false,
              showsZoomControl: false
            });
            map.region = new window.mapkit.CoordinateRegion(
              start,
              new window.mapkit.CoordinateSpan(0.01, 0.01)
            );
            marker = new window.mapkit.MarkerAnnotation(start, {
              title: markerTitle,
              color: "#D7AA54"
            });
            map.addAnnotation(marker);
            log("map create complete", {
              width: element.clientWidth,
              height: element.clientHeight
            });
          } catch (error) {
            errorLog("map create failed", error);
            return;
          }

          let pressState = null;
          const longPressMs = 650;
          const panTolerancePx = 10;
          element.addEventListener("pointerdown", event => {
            pressState = {
              pointerId: event.pointerId,
              pageX: event.pageX,
              pageY: event.pageY,
              startedAt: Date.now(),
              moved: false
            };
            log("pointerdown", { pageX: event.pageX, pageY: event.pageY, pointerId: event.pointerId });
          });
          element.addEventListener("pointermove", event => {
            if (!pressState || pressState.pointerId !== event.pointerId) return;
            const dx = event.pageX - pressState.pageX;
            const dy = event.pageY - pressState.pageY;
            if (Math.sqrt(dx * dx + dy * dy) > panTolerancePx) {
              pressState.moved = true;
            }
          });
          element.addEventListener("pointerup", event => {
            if (!pressState || pressState.pointerId !== event.pointerId) return;
            const heldMs = Date.now() - pressState.startedAt;
            const wasLongPress = heldMs >= longPressMs;
            const wasMoved = pressState.moved;
            const startX = pressState.pageX;
            const startY = pressState.pageY;
            pressState = null;
            if (!wasLongPress || wasMoved) {
              log("pointerup ignored", { heldMs, moved: wasMoved });
              return;
            }
            if (!map || typeof map.convertPointOnPageToCoordinate !== "function") {
              log("long press ignored", { hasMap: !!map, canConvert: !!(map && typeof map.convertPointOnPageToCoordinate === "function") });
              return;
            }
            const point = new DOMPoint(startX, startY);
            const next = map.convertPointOnPageToCoordinate(point);
            if (!next) {
              log("long press no coordinate");
              return;
            }
            log("long press coordinate", { lat: next.latitude, lng: next.longitude });
            setMarker(next.latitude, next.longitude, false);
            onCoordinateChanged(next.latitude, next.longitude);
          });
          ["pointercancel", "pointerleave"].forEach(type => {
            element.addEventListener(type, () => {
              pressState = null;
            });
          });

          if (pendingSearch) search(pendingSearch);
        });
      }, 0);

      return controller;
    }
        """
    )
    return factory(element, token, markerTitle, latitude, longitude, onCoordinateChanged)
}

private fun Double.formatCoordinate(): String =
    (kotlin.math.round(this * 100000.0) / 100000.0).toString()
